"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DataFilter, type FilterValues } from "@/components/data-filter";
import { DataTable, type ColumnDef } from "@/components/data-table";
import { DataSummary } from "@/components/data-summary";
import { DetailDialog } from "@/components/detail-dialog";
import { fetchFaDetails, exportFaDetails, fetchFaDetailById } from "./actions";
import { FA_COLUMNS } from "./columns";
import type { FaDetailRow } from "@/lib/query";
import { Download, Eye } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "3", label: "3" },
  { value: "6", label: "6" },
  { value: "10", label: "10" },
  { value: "13", label: "13" },
  { value: "17", label: "17" },
  { value: "20", label: "20" },
  { value: "24", label: "24" },
  { value: "27", label: "27" },
  { value: "29", label: "29" },
  { value: "31", label: "31" },
];

const MONEY_KEYS = ["amount", "total_amount", "tax_amount", "unit_price"];

const TABLE_COLUMNS: ColumnDef<FaDetailRow>[] = FA_COLUMNS.map((col) => ({
  key: col.key,
  header: col.label,
  className: col.key === "vendor_name" || col.key === "material_name" || col.key === "material_spec"
    ? "min-w-[150px]"
    : MONEY_KEYS.includes(col.key)
      ? "text-right min-w-[100px]"
      : undefined,
  render: MONEY_KEYS.includes(col.key)
    ? (row: FaDetailRow) => {
        const val = row[col.key as keyof FaDetailRow];
        return val !== null ? `¥${Number(val).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}` : "";
      }
    : undefined,
}));

const DETAIL_FIELDS: { key: string; label: string; type?: "text" | "money" | "date" | "json" }[] = [
  { key: "query_start_date", label: "查询起始日期", type: "date" },
  { key: "query_end_date", label: "查询截止日期", type: "date" },
  { key: "document_number", label: "单据号" },
  { key: "document_date", label: "单据日期", type: "date" },
  { key: "vendor_code", label: "供应商编码" },
  { key: "vendor_name", label: "供应商名称" },
  { key: "material_code", label: "物料编码" },
  { key: "material_name", label: "物料名称" },
  { key: "material_spec", label: "物料规格" },
  { key: "unit", label: "单位" },
  { key: "quantity", label: "数量" },
  { key: "unit_price", label: "单价", type: "money" },
  { key: "amount", label: "金额", type: "money" },
  { key: "currency", label: "币种" },
  { key: "tax_rate", label: "税率" },
  { key: "tax_amount", label: "税额", type: "money" },
  { key: "total_amount", label: "总金额", type: "money" },
  { key: "status", label: "状态" },
  { key: "approver", label: "审批人" },
  { key: "approval_date", label: "审批日期", type: "date" },
  { key: "payment_status", label: "付款状态" },
  { key: "payment_date", label: "付款日期", type: "date" },
  { key: "business_unit", label: "业务单元" },
  { key: "cost_center", label: "成本中心" },
  { key: "project_code", label: "项目编码" },
  { key: "description", label: "描述" },
  { key: "remarks", label: "备注" },
  { key: "batch_id", label: "批次号" },
  { key: "extraction_date", label: "提取日期", type: "date" },
  { key: "source_url", label: "来源链接" },
];

export default function FaDetailsPage() {
  const [filters, setFilters] = useState<FilterValues>({
    documentNumber: "",
    vendorName: "",
    startDate: "",
    endDate: "",
    status: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<FaDetailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ count: 0, totalAmount: 0, taxAmount: 0, grandTotal: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null);
  const [detailRowJson, setDetailRowJson] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFaDetails(filters, page, pageSize);
      setData(result.rows);
      setTotal(result.total);
      setSummary(result.summary);
    } catch (err) {
      console.error("查询失败:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function openDetail(row: FaDetailRow) {
    try {
      const detail = await fetchFaDetailById(row.id);
      setDetailRow(detail);
      setDetailRowJson(detail.row_json ?? null);
      setDetailOpen(true);
    } catch (err) {
      console.error("获取详情失败:", err);
    }
  }

  function handleSearch() {
    setPage(1);
    loadData();
  }

  function handlePageSizeChange(size: number) {
    setPageSize(size);
    setPage(1);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const result = await exportFaDetails(filters);
      const blob = new Blob([result.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("导出失败:", err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex-1 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">FA 固定资产明细</h1>
        <Button onClick={handleExport} disabled={exporting} size="sm">
          <Download className="h-4 w-4 mr-1" />
          {exporting ? "导出中..." : "导出 CSV"}
        </Button>
      </div>

      <DataFilter
        filters={filters}
        onFiltersChange={setFilters}
        statusOptions={STATUS_OPTIONS}
      />

      <Button onClick={handleSearch} disabled={loading} size="sm" className="w-full md:w-auto">
        {loading ? "查询中..." : "查询"}
      </Button>

      <DataSummary summary={summary} />

      <DataTable
        columns={TABLE_COLUMNS}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        actionRender={(row) => (
          <Button variant="outline" size="sm" onClick={() => openDetail(row)}>
            <Eye className="h-3 w-3 mr-1" />
            详情
          </Button>
        )}
      />

      <DetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        title="FA 固定资产明细详情"
        row={detailRow}
        fields={DETAIL_FIELDS}
        rowJson={detailRowJson}
      />
    </div>
  );
}